/**
 * Panel Ambient Module — Looping background atmosphere, per panel, with crossfade
 *
 * Purpose:
 * - Each panel may declare its own looping ambience via `data-ambient="URL"`
 *   on the .story-panel element. On panel change the ambience crossfades.
 * - Runs OUTSIDE the StoryMediaManager (NOT subject to single-medium
 *   enforcement) — so it plays in parallel to the panel O-Ton (story-panel-audio)
 *   and the looping muted video.
 * - Ducks its volume while another medium (O-Ton, bubbles, …) plays
 *   (story:media:ducking / unducking), and offers a mute toggle.
 *
 * Behaviour contract (decided 2026-06-13):
 * - Same URL across consecutive panels  → keeps playing seamlessly (no restart,
 *   no click). The crossfade only fires on a real URL change.
 * - Empty / missing data-ambient        → the running ambience fades to silence.
 * - Fast scrolling                      → a ~200 ms debounce: the switch only
 *   happens once the target panel settles. Intermediate panels skimmed past do
 *   not trigger a cascade of crossfades.
 *
 * Two audio "decks" (A/B) alternate so a fade-out and fade-in overlap cleanly.
 * Each deck owns at most one fade timer (clearInterval on reassignment), so a
 * superseded switch can never leave two fades fighting on the same element.
 *
 * Configuration (optional, via Code Injection):
 *   window.storyMeta.panelAmbientVolume     = 0.4;   // base volume (0-1)
 *   window.storyMeta.panelAmbientDuckVolume = 0.12;  // ducked volume
 *
 * Lifecycle:
 *   1. init() — inactive unless at least one .story-panel[data-ambient] exists.
 *   2. Waits for user gesture (story:started from the loader) before playing.
 *   3. Reacts to story:panel:activated to pick each panel's ambience.
 *
 * Coexistence: does NOT create a second mute toggle if the global ambient
 * module (story-module-ambient.js) already placed one. A tour should use either
 * a global ambience (storyMeta.ambientSrc) OR per-panel ambience, not both.
 *
 * Dependencies: Active Panel system (story:panel:activated events).
 * File: assets/js/story/story-module-panel-ambient.js
 */
(function (window) {
  'use strict';

  var DEBUG = window.location && window.location.search.indexOf('debug=story') !== -1;
  function log() {
    if (DEBUG && window.console) {
      console.log.apply(console, ['[PanelAmbient]'].concat([].slice.call(arguments)));
    }
  }

  var FADE_MS = 900;            // crossfade / fade-to-silence duration
  var DUCK_FADE_MS = 300;       // fade when ducking down
  var UNDUCK_FADE_MS = 500;     // fade when restoring after duck
  var SWITCH_DEBOUNCE_MS = 200; // guard against crossfade stutter while fast-scrolling
  var FADE_STEP_MS = 50;        // fade tick resolution

  function StoryPanelAmbient() {
    this.decks = [];          // two HTMLAudioElements
    this.activeDeck = -1;     // index of the sounding deck, -1 = silence
    this.currentSrc = null;   // URL the active deck is playing (null = silence)
    this.pendingSrc = null;   // last requested target URL (for gesture-gate + debounce)

    this.baseVolume = 0.4;
    this.duckVolume = 0.12;

    this.started = false;     // user gesture received → playback allowed
    this.isMuted = false;
    this.isDucked = false;

    this._fadeTimers = [null, null];
    this._switchTimer = null;

    this.toggleBtn = null;
    this._ownsToggle = false;
  }

  /* ========================================
     Init (called from story-init.js)
     ======================================== */

  StoryPanelAmbient.prototype.init = function () {
    // Inactive unless a panel actually declares an ambience.
    if (!document.querySelector('.story-panel[data-ambient]')) {
      log('no [data-ambient] panels — module inactive');
      return;
    }

    // Doppel-Atmo-Fix (Live-Befund Nadelöhr 2026-07-24): nb-Touren haben
    // ihre EIGENE Atmo-Engine (story-module-nbscrolly.js Block 6/7), die
    // dieselben data-ambient-Attribute liest. Lief dieses Modul dort auch,
    // spielte jede Atmo DOPPELT (zwei Deck-Systeme, phasenversetzt) und es
    // gab zwei Ton-Knöpfe übereinander — der sichtbare mutete nur eines
    // der Systeme („Knopf funktioniert nicht"). nb besitzt seine Seiten.
    if (document.querySelector('.nb-scrolly[data-ambient]')) {
      log('nb-Atmo-Engine besitzt diese Seite — module inactive');
      return;
    }

    var meta = window.storyMeta || {};
    if (typeof meta.panelAmbientVolume === 'number') this.baseVolume = meta.panelAmbientVolume;
    if (typeof meta.panelAmbientDuckVolume === 'number') this.duckVolume = meta.panelAmbientDuckVolume;

    this.decks = [this._makeDeck(), this._makeDeck()];
    this._createToggleUI();

    var self = this;

    window.addEventListener('story:panel:activated', function (e) {
      var panel = e.detail && e.detail.panel;
      if (!panel) return;
      // null (attribute missing) OR '' (empty) → silence
      var url = panel.getAttribute('data-ambient');
      self._requestSrc(url ? url : null);
    });

    window.addEventListener('story:media:ducking', function () { self._onDuck(); });
    window.addEventListener('story:media:unducking', function () { self._onUnduck(); });

    // Playback may only begin after a user gesture (autoplay policy).
    window.addEventListener('story:started', function () {
      self.started = true;
      self._applyPending();
    });
    // Fallback: gesture already happened before this module initialised.
    if (window.StoryMediaManager && window.StoryMediaManager.userGestureReceived) {
      this.started = true;
    }

    log('init — baseVol', this.baseVolume, 'duckVol', this.duckVolume);
  };

  StoryPanelAmbient.prototype._makeDeck = function () {
    var a = new Audio();
    a.loop = true;
    a.preload = 'auto';
    a.volume = 0;
    return a;
  };

  /* ========================================
     Source selection (gesture-gate + debounce)
     ======================================== */

  // Called on every panel activation with the panel's ambience URL (or null).
  StoryPanelAmbient.prototype._requestSrc = function (url) {
    // Target unchanged from the last request → nothing new to schedule.
    // (Crucially: re-entering panels that share the current URL is a no-op,
    //  so the ambience keeps playing seamlessly.)
    if (url === this.pendingSrc) return;
    this.pendingSrc = url;

    // Before the user gesture: only remember; start happens via story:started.
    if (!this.started) return;

    this._scheduleSwitch();
  };

  StoryPanelAmbient.prototype._scheduleSwitch = function () {
    // Already sounding the requested URL → seamless continuation, cancel any
    // pending switch (e.g. user scrolled away and back again).
    if (this.pendingSrc === this.currentSrc) {
      if (this._switchTimer) { clearTimeout(this._switchTimer); this._switchTimer = null; }
      return;
    }
    // Debounce: only switch once the target has been stable for ~200 ms, so
    // skimming past panels does not trigger a burst of crossfades.
    var self = this;
    if (this._switchTimer) clearTimeout(this._switchTimer);
    this._switchTimer = setTimeout(function () {
      self._switchTimer = null;
      if (self.pendingSrc === self.currentSrc) return;
      self._crossfadeTo(self.pendingSrc);
    }, SWITCH_DEBOUNCE_MS);
  };

  // On story start: apply the current target immediately (no debounce — this is
  // the first ambience, there is nothing to stutter past).
  StoryPanelAmbient.prototype._applyPending = function () {
    if (this.pendingSrc === this.currentSrc) return;
    this._crossfadeTo(this.pendingSrc);
  };

  /* ========================================
     Crossfade
     ======================================== */

  StoryPanelAmbient.prototype._crossfadeTo = function (url) {
    var targetVol = this.isMuted ? 0 : (this.isDucked ? this.duckVolume : this.baseVolume);

    // Fade to silence.
    if (url === null) {
      if (this.activeDeck !== -1) this._fadeDeck(this.activeDeck, 0, FADE_MS, true);
      this.activeDeck = -1;
      this.currentSrc = null;
      log('→ silence');
      return;
    }

    var incoming = (this.activeDeck === -1) ? 0 : (1 - this.activeDeck);
    var outgoing = this.activeDeck;

    var deck = this.decks[incoming];
    this._clearFade(incoming);
    if (deck.getAttribute('src') !== url) deck.src = url;
    try { deck.currentTime = 0; } catch (e) {}
    deck.volume = 0;
    var p = deck.play();
    if (p && p.catch) p.catch(function () { log('ambient autoplay blocked — waits for gesture'); });

    this._fadeDeck(incoming, targetVol, FADE_MS, false);
    if (outgoing !== -1 && outgoing !== incoming) {
      this._fadeDeck(outgoing, 0, FADE_MS, true);
    }

    this.activeDeck = incoming;
    this.currentSrc = url;
    this._showToggle();
    log('crossfade → deck', incoming, url);
  };

  /* ========================================
     Per-deck fade helper
     ======================================== */

  StoryPanelAmbient.prototype._clearFade = function (deckIndex) {
    if (this._fadeTimers[deckIndex]) {
      clearInterval(this._fadeTimers[deckIndex]);
      this._fadeTimers[deckIndex] = null;
    }
  };

  // Fades one deck to targetVol. pauseAtEnd: pause the deck once it reaches 0.
  StoryPanelAmbient.prototype._fadeDeck = function (deckIndex, targetVol, durationMs, pauseAtEnd) {
    var deck = this.decks[deckIndex];
    if (!deck) return;
    this._clearFade(deckIndex);

    var steps = Math.max(1, Math.round(durationMs / FADE_STEP_MS));
    var startVol = deck.volume;
    var delta = (targetVol - startVol) / steps;
    var step = 0;
    var self = this;

    this._fadeTimers[deckIndex] = setInterval(function () {
      step++;
      if (step >= steps) {
        deck.volume = Math.max(0, Math.min(1, targetVol));
        self._clearFade(deckIndex);
        if (pauseAtEnd && targetVol === 0) {
          try { deck.pause(); } catch (e) {}
        }
      } else {
        deck.volume = Math.max(0, Math.min(1, startVol + delta * step));
      }
    }, FADE_STEP_MS);
  };

  /* ========================================
     Ducking (other medium playing)
     ======================================== */

  StoryPanelAmbient.prototype._onDuck = function () {
    this.isDucked = true;
    if (this.activeDeck === -1 || this.isMuted) return;
    this._fadeDeck(this.activeDeck, this.duckVolume, DUCK_FADE_MS, false);
  };

  StoryPanelAmbient.prototype._onUnduck = function () {
    this.isDucked = false;
    if (this.activeDeck === -1 || this.isMuted) return;
    this._fadeDeck(this.activeDeck, this.baseVolume, UNDUCK_FADE_MS, false);
  };

  /* ========================================
     Mute toggle
     ======================================== */

  StoryPanelAmbient.prototype.toggleMute = function () {
    this.isMuted = !this.isMuted;
    if (this.activeDeck !== -1) {
      var vol = this.isMuted ? 0 : (this.isDucked ? this.duckVolume : this.baseVolume);
      this._fadeDeck(this.activeDeck, vol, 200, false);
    }
    this._updateToggleUI();
    log(this.isMuted ? 'muted' : 'unmuted');
  };

  /* ========================================
     Toggle button UI (reuses .ambient-toggle CSS)
     ======================================== */

  StoryPanelAmbient.prototype._createToggleUI = function () {
    // Don't add a second toggle if the global ambient module already placed one.
    if (document.querySelector('.ambient-toggle')) {
      this._ownsToggle = false;
      return;
    }
    var btn = document.createElement('button');
    btn.className = 'ambient-toggle';
    btn.setAttribute('aria-label', 'Hintergrund-Atmo ein/aus');
    btn.setAttribute('title', 'Hintergrund-Atmo ein/aus');
    btn.innerHTML = this._speakerIcon(true);
    btn.style.display = 'none'; // shown once ambience actually plays

    var self = this;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      self.toggleMute();
    });

    document.body.appendChild(btn);
    this.toggleBtn = btn;
    this._ownsToggle = true;
  };

  StoryPanelAmbient.prototype._showToggle = function () {
    if (this._ownsToggle && this.toggleBtn) this.toggleBtn.style.display = '';
  };

  StoryPanelAmbient.prototype._updateToggleUI = function () {
    if (!this._ownsToggle || !this.toggleBtn) return;
    this.toggleBtn.innerHTML = this._speakerIcon(!this.isMuted);
    this.toggleBtn.classList.toggle('is-muted', this.isMuted);
  };

  StoryPanelAmbient.prototype._speakerIcon = function (on) {
    if (on) {
      return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
        '<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>' +
        '<path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>' +
        '<path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>' +
        '</svg>';
    }
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>' +
      '<line x1="23" y1="9" x2="17" y2="15"/>' +
      '<line x1="17" y1="9" x2="23" y2="15"/>' +
      '</svg>';
  };

  /* ========================================
     Cleanup
     ======================================== */

  StoryPanelAmbient.prototype.destroy = function () {
    if (this._switchTimer) clearTimeout(this._switchTimer);
    this._clearFade(0);
    this._clearFade(1);
    this.decks.forEach(function (d) {
      try { d.pause(); d.src = ''; } catch (e) {}
    });
    this.decks = [];
    if (this._ownsToggle && this.toggleBtn && this.toggleBtn.parentNode) {
      this.toggleBtn.parentNode.removeChild(this.toggleBtn);
    }
    this.activeDeck = -1;
    this.currentSrc = null;
  };

  /* ========================================
     Expose
     ======================================== */

  window.StoryModulePanelAmbient = StoryPanelAmbient;

})(window);
